/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * SU-5 / N-13 (S663): "which currency did this customer actually pay in", once.
 *
 * This rule was written in `invoicesAdapter.ts` and GUESSED a second time in the
 * Subscriptions row, from the currency the browser detected for whoever happens
 * to be looking at the page. Those are different questions, and the browser's
 * answer is wrong in three ordinary ways: a customer signed in from a browser
 * whose locale does not match the card they paid with sees an estimate in the
 * wrong currency; a customer viewing a dollar-billed subscription from a
 * euro-zone browser is offered a euro estimate for a subscription that will never
 * be converted; and a customer whose browser reports dollars sees nothing at all
 * for a subscription that will be billed in euros.
 *
 * The shop is TOLD this, it does not have to infer it. Stripe records the
 * presentment pair on the CHARGE (and on the checkout session, the payment intent
 * and the refund) -- never on the Subscription. This shop copies it onto the
 * invoice at checkout and again at each renewal, and there is a backfill command
 * in the package for invoices that predate that. So the invoice is the source,
 * and both the Invoices table and the Subscriptions row now read it from here.
 *
 * An earlier round of this work proposed reading `subscription.presentment_details`.
 * That field does not exist, so the line would have rendered for nobody and the
 * fix would have looked done while doing nothing. It is recorded here because
 * two separate attempts reached for it.
 */

export interface InvoicePresentmentSource {
  currency?: string | null;
  amount_paid?: number;
  metadata?: Record<string, unknown> | null;
  display_currency?: string | null;
  display_amount?: number | null;
}

export interface InvoicePresentment {
  /** Lower-case ISO currency the customer was actually billed in. */
  currency: string;
  /** Amount in the smallest unit of that currency. */
  amount: number;
  /**
   * True when the pair came from a recorded presentment source rather than
   * from the invoice's own integration currency. False means "nothing was
   * recorded", which for a dollar-billed customer is the ordinary case.
   */
  resolved: boolean;
}

/**
 * B-4 (FCR S316 Option B): Read Adaptive Pricing presentment data from an invoice.
 *
 * For one-time purchases, the checkout handler persists presentment_amount and
 * presentment_currency to invoice metadata so the dashboard can show the customer
 * the currency they actually paid in.
 *
 * S663: the comment that stood here said subscription RENEWAL invoices do not
 * carry this metadata and fall back to the integration currency. That is out of
 * date: the renewal handler resolves the presentment pair from the charge and
 * writes it onto the renewal invoice too, exactly as checkout does, so renewals
 * resolve like any other invoice. Corrected rather than left, because the next
 * reader would otherwise build on it -- which is how the Subscriptions row came
 * to guess from the browser in the first place.
 */
export function readInvoicePresentment(
  invoice: InvoicePresentmentSource | null | undefined,
): InvoicePresentment {
  // SU-10 / NF-8 (FCR Pass 9): the server now settles this and sends the answer
  // as display_currency and display_amount. Prefer it.
  //
  // The browser cannot decide this on its own. When this shop's own presentment
  // metadata is missing, the only other record is on the charge -- and on this
  // API version a charge is reached through payments, then payment, then payment
  // intent, then latest charge. That is five expansion levels against a cap of
  // four, so the charge can never appear in an invoice list response no matter
  // what is requested. Around 40 in 100 customers could not match an invoice to
  // their card statement because of that gap.
  //
  // The metadata read below is kept as a fallback for payloads that predate the
  // server change, so an older cached response still renders correctly.
  const displayCurrency = invoice?.display_currency;
  const displayAmount = invoice?.display_amount;
  if (
    displayCurrency &&
    typeof displayAmount === "number" &&
    Number.isFinite(displayAmount)
  ) {
    return {
      currency: displayCurrency.toLowerCase(),
      amount: displayAmount,
      resolved: true,
    };
  }

  const meta = (invoice?.metadata ?? {}) as Record<string, string | undefined>;
  const pCur = meta.presentment_currency;
  const pAmtRaw = meta.presentment_amount;
  if (pCur && pAmtRaw) {
    const pAmt = parseInt(pAmtRaw, 10);
    if (Number.isFinite(pAmt)) {
      return { currency: pCur.toLowerCase(), amount: pAmt, resolved: true };
    }
  }
  return {
    currency: (invoice?.currency || "usd").toLowerCase(),
    amount: invoice?.amount_paid ?? 0,
    resolved: false,
  };
}
